import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  effect,
} from '@angular/core';
import { paginationParams } from '@shared/config/table.config';
import { CandidatesService, VendorCandidatesQuery } from './candidates.service';
import { FileDownloadService } from '@services/file-download.service';
import { PersonaType } from '@constants/global.enum';
import { dateFormat } from '@constants/dateFormat';

@Component({
  selector: 'app-candidates',
  templateUrl: './candidates.component.html',
  styleUrl: './candidates.component.scss',
})
export class CandidatesComponent implements OnInit {
  @Output() candidatesCountChange = new EventEmitter<number>();
  @Input() candidatesCount!: number;

  imageSrc: string = 'assets/img/no-data-found.png';
  displayCandidatesData: any[] = [];
  fieldsVisibility: string = PersonaType.vendorCandidates;
  dateFormat = dateFormat;
  isDataLoaded = false;

  // server-driven state
  activeFilters: VendorCandidatesQuery = {};
  facets: { [key: string]: string[] } = {};
  pageSize = paginationParams.pageSize;
  totalRecords = 0;
  sortColumn = '';
  sortDir: 'asc' | 'desc' = 'asc';
  filterValue = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // filter panel
  showFilterPanel = false;
  filterDraft = { status: '', jobTitle: '', location: '' };

  // hover tooltip
  hoverTip = { show: false, text: '', x: 0, y: 0 };

  constructor(
    private candidatesService: CandidatesService,
    private fileDownloadService: FileDownloadService,
    private cdr: ChangeDetectorRef,
  ) {
    effect(() => {
      const signalData = this.candidatesService.candidatesSignal();
      const payload = signalData?.data;
      if (payload?.data) {
        this.displayCandidatesData = payload.data;
        this.totalRecords = payload.totalRecords;
        this.candidatesCount = payload.totalRecords;
        this.candidatesCountChange.emit(payload.totalRecords);
        this.isDataLoaded = true;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit() {
    // Drop the previous visit's cached payload so re-entering the tab doesn't
    // flash stale rows behind the spinner while the fresh page loads.
    this.candidatesService.candidatesSignal.set(null);
    this.activeFilters = { page: 1, limit: this.pageSize };
    this.loadPage();
    this.candidatesService.getCandidateFacets().subscribe((f) => {
      this.facets = f || {};
      this.cdr.markForCheck();
    });
  }

  private loadPage(): void {
    // Clear the table while fetching — a loading message shows instead of stale rows.
    this.isDataLoaded = false;
    this.displayCandidatesData = [];
    this.cdr.markForCheck();
    this.candidatesService.getCandidates(this.activeFilters);
  }

  // ---- search ----
  searchItem(event: KeyboardEvent): void {
    this.filterValue = (event.target as HTMLInputElement)?.value ?? '';
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.activeFilters = { ...this.activeFilters, q: this.filterValue?.trim() || undefined, page: 1 };
      this.loadPage();
    }, 400);
  }

  clearInput(): void {
    this.filterValue = '';
    this.activeFilters = { ...this.activeFilters, q: undefined, page: 1 };
    this.loadPage();
  }

  // ---- sort ----
  sortBy(field: string): void {
    if (this.sortColumn === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = field;
      this.sortDir = 'asc';
    }
    this.activeFilters = { ...this.activeFilters, sort: this.sortColumn, dir: this.sortDir, page: 1 };
    this.loadPage();
  }

  sortIndicator(field: string): string {
    return this.sortColumn === field ? this.sortDir : '';
  }

  // ---- filter panel ----
  get facetStatus(): string[] { return this.facets['status'] || []; }
  get facetJobTitle(): string[] { return this.facets['jobTitle'] || []; }
  get facetLocation(): string[] { return this.facets['location'] || []; }

  get activeFilterCount(): number {
    const f = this.activeFilters;
    let n = 0;
    if (f.status) n++;
    if (f.jobTitle) n++;
    if (f.location) n++;
    return n;
  }

  toggleFilterPanel(): void {
    if (!this.showFilterPanel) {
      this.filterDraft = {
        status: this.activeFilters.status || '',
        jobTitle: this.activeFilters.jobTitle || '',
        location: this.activeFilters.location || '',
      };
    }
    this.showFilterPanel = !this.showFilterPanel;
  }

  applyFilterPanel(): void {
    const d = this.filterDraft;
    this.activeFilters = {
      ...this.activeFilters,
      status: d.status || undefined,
      jobTitle: d.jobTitle || undefined,
      location: d.location || undefined,
      page: 1,
    };
    this.showFilterPanel = false;
    this.loadPage();
  }

  resetFilterPanel(): void {
    this.filterDraft = { status: '', jobTitle: '', location: '' };
    this.activeFilters = {
      page: 1,
      limit: this.pageSizeValue,
      q: this.activeFilters.q,
      sort: this.activeFilters.sort,
      dir: this.activeFilters.dir,
    };
    this.showFilterPanel = false;
    this.loadPage();
  }

  // ---- pagination ----
  get currentPageValue(): number { return this.activeFilters.page ?? 1; }
  get pageSizeValue(): number { return this.activeFilters.limit ?? this.pageSize; }
  get totalPages(): number { return Math.max(1, Math.ceil((this.totalRecords || 0) / this.pageSizeValue)); }
  get rangeStart(): number { return this.totalRecords === 0 ? 0 : (this.currentPageValue - 1) * this.pageSizeValue + 1; }
  get rangeEnd(): number { return Math.min(this.currentPageValue * this.pageSizeValue, this.totalRecords); }

  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.currentPageValue;
    const out: number[] = [];
    if (total <= 7) { for (let i = 1; i <= total; i++) out.push(i); return out; }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPageValue) return;
    this.activeFilters = { ...this.activeFilters, page };
    this.loadPage();
  }

  prevPage(): void { this.goToPage(this.currentPageValue - 1); }
  nextPage(): void { this.goToPage(this.currentPageValue + 1); }

  onPageSizeChange(event: Event): void {
    const size = +(event.target as HTMLSelectElement).value;
    this.activeFilters = { ...this.activeFilters, limit: size, page: 1 };
    this.loadPage();
  }

  trackByIndex(index: number): number { return index; }

  // ---- cell helpers ----
  statusClass(status?: string): string {
    const v = (status || '').toLowerCase();
    if (v.includes('select') || v.includes('onboard') || v.includes('hire') || v.includes('offer')) return 'open';
    if (v.includes('reject') || v.includes('declin') || v.includes('withdraw')) return 'closed';
    return 'hold';
  }

  agingClass(days?: number): string {
    const a = +(days || 0);
    return a > 90 ? 'stale' : a >= 30 ? 'mod' : 'fresh';
  }

  candidateLocation(row: any): string {
    const loc = row?.location;
    if (!loc) return '';
    if (Array.isArray(loc)) {
      return loc
        .map((l: any) => (typeof l === 'string' ? l : l?.location || l?.name || ''))
        .filter(Boolean)
        .join(', ');
    }
    return String(loc);
  }

  showTip(event: MouseEvent, text?: string | null): void {
    if (!text) return;
    const cell = event.currentTarget as HTMLElement;
    const inner = (cell.querySelector('.op-tip-txt') as HTMLElement) || cell;
    if (inner.scrollWidth <= inner.clientWidth + 1) return;
    const rect = cell.getBoundingClientRect();
    this.hoverTip = { show: true, text, x: rect.left, y: rect.bottom + 4 };
  }

  hideTip(): void { this.hoverTip.show = false; }

  // ---- preserved behaviours ----
  receiveData(data: string, candidateEmailId: string) {
    this.displayCandidatesData.forEach((item) => {
      if (item.emailId === candidateEmailId) {
        item.status = data;
      }
    });
  }

  downloadFile(filePath: string, fileName: string, fileType: string) {
    const fullFileName = fileType + '_' + fileName + '.pdf';
    this.fileDownloadService.downloadFile(filePath, fullFileName);
  }
}
